import { RankedSubmission } from "@/api/generated/backendAPI.schemas";
import {
  formatTimeAgo,
  getShortenedScore,
  getShortenedTimeAndMemory,
} from "@/utils/utils";
import { BiSolidMedal } from "react-icons/bi";
import { Link } from "@tanstack/react-router";
import UserImage from "../ui/UserImage";
import HelpTip from "../ui/HelpTip";
import "@/components/components.scss";

type Props = {
  rankingData: readonly RankedSubmission[];
  submissionType: string | undefined;
  /** チーム戦の開催回ならチーム名の列を出す */
  showTeam?: boolean;
};
const thStyle =
  "px-4 py-3 text-center text-[#111518] text-sm font-medium leading-normal whitespace-nowrap";

function Ranking({ rankingData, submissionType, showTeam = false }: Props) {
  return (
    <div className="px-4 py-3 @container">
      <div className="flex overflow-hidden rounded-xl border border-[#dbe1e6] bg-white">
        <table className="flex-1">
          <thead>
            <tr className="bg-white">
              <th
                className={
                  submissionType === "program"
                    ? "w-1/8 " + thStyle
                    : "w-[12%] " + thStyle
                }
              >
                ランク
              </th>
              <th
                className={
                  submissionType === "program"
                    ? "w-1/4 " + thStyle
                    : "w-1/4 " + thStyle
                }
              >
                投稿者
              </th>
              {showTeam && <th className={thStyle}>チーム</th>}
              <th
                className={
                  submissionType === "program"
                    ? "w-1/4 " + thStyle
                    : "w-1/4 " + thStyle
                }
              >
                ベスト投稿日時
              </th>
              <th
                className={
                  submissionType === "program"
                    ? "w-1/8 " + thStyle
                    : "w-[12%] " + thStyle
                }
              >
                スコア
              </th>
              <th className={thStyle}>
                投稿回数
                <HelpTip text="このコンペへの投稿の回数です。採点でエラーになった投稿も含みます。" />
              </th>
              <th className={thStyle}>最終投稿</th>
              {submissionType === "program" && (
                <>
                  <th className="px-4 py-3 text-center text-[#111518] w-1/8 text-sm font-medium leading-normal">
                    処理時間
                    <HelpTip text="プログラムの実行に要した時間（ミリ秒）です。参考情報として表示しています。スコアへの影響はありません。" />
                  </th>
                  <th className="px-4 py-3 text-center text-[#111518] w-1/8 text-sm font-medium leading-normal">
                    メモリ使用量
                    <HelpTip text="プログラムの実行時に計測したメモリ使用量です。参考情報として表示しています。スコアへの影響はありません。" />
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rankingData.map((data) => {
              const createdAt = data.createdAt
                ? new Date(data.createdAt)
                : null;
              return (
                <tr
                  className="border-t border-t-[#dbe1e6]"
                  key={data.submissionId}
                >
                  <td className="h-[72px] px-4 py-2 text-sm leading-normal">
                    <div className="flex items-center justify-center gap-4">
                      {data.rankNum === 1 ? (
                        <BiSolidMedal color="#FFD700" size={24} />
                      ) : data.rankNum === 2 ? (
                        <BiSolidMedal color="#C0C0C0" size={24} />
                      ) : data.rankNum === 3 ? (
                        <BiSolidMedal color="#DA744B" size={24} />
                      ) : (
                        <div className="size-6"></div>
                      )}
                      <p>No. {data.rankNum}</p>
                    </div>
                  </td>
                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal gap-4">
                    <div className="flex items-center justify-center gap-4">
                      <div className="size-10 shrink-0">
                        <UserImage
                          noImageType=""
                          userId={data.userId}
                          size={10}
                        />
                      </div>
                      <div className="w-32 shrink-0">
                        <Link
                          to="/profile/$userId"
                          params={{ userId: data.userId }}
                        >
                          {data.userName}
                        </Link>
                      </div>
                    </div>
                  </td>
                  {showTeam && (
                    <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal text-center whitespace-nowrap">
                      {data.teamName ?? "-"}
                    </td>
                  )}
                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                    <div className="flex items-center justify-center gap-4">
                      {createdAt ? createdAt.toLocaleString() : ""}
                    </div>
                  </td>
                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                    <div className="flex items-center justify-center gap-4">
                      <p className="score">
                        {data.score != null
                          ? getShortenedScore(data.score)
                          : ""}
                        <span className="hidden">{data.score}</span>
                      </p>
                    </div>
                  </td>
                  <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal text-center tabular-nums">
                    {data.submissionCount}
                  </td>
                  <td
                    className="h-[72px] px-4 py-2 text-sm font-normal leading-normal text-center text-gray-600"
                    title={
                      data.lastSubmittedAt
                        ? new Date(data.lastSubmittedAt).toLocaleString()
                        : undefined
                    }
                  >
                    <span className="whitespace-nowrap">
                      {formatTimeAgo(data.lastSubmittedAt)}
                    </span>
                  </td>
                  {submissionType === "program" && (
                    <>
                      <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                        <p className="flex items-center justify-center">
                          {data.runnningTime
                            ? getShortenedTimeAndMemory(data.runnningTime) +
                              " sec"
                            : ""}
                        </p>
                      </td>
                      <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                        <p className="flex items-center justify-center">
                          {data.memoryUsage
                            ? getShortenedTimeAndMemory(data.memoryUsage) +
                              " MB"
                            : ""}
                        </p>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Ranking;
